import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../context/AppContext";
import { ago } from "../../lib/fleet";
import { nameOf, useProductNames } from "../../lib/products";

// Training on this cabinet (docs/tasks/35, phase 4): the products of its layout
// against its type's rows, a visit in progress, the last visit and what waits
// for Update shelves. Visits themselves are run from the capture app.

function Fields({ obj }) {
  return <span className="font-mono text-xs">{Object.entries(obj || {}).map(([k, v]) => `${k} ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</span>;
}

export function TrainingTab({ deviceId }) {
  const api = useApi();
  const names = useProductNames(api);
  const [st, setSt] = useState(undefined);
  const load = () => api.getDeviceTrainingStatus(deviceId).then(setSt).catch(() => setSt(null));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId]);
  // While a visit or its learning runs, the status changes every few seconds.
  useEffect(() => {
    if (!st?.live && !st?.collecting) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st]);

  if (st === undefined) return <p className="text-sm text-slate-500">Loading…</p>;
  if (st === null) return <p className="text-sm text-slate-500">No training status for this cabinet.</p>;
  if (!st.cabinet_type_id) return <p className="text-sm text-amber-700">The cabinet has no type: training rows belong to a type, so assign one first (Overview).</p>;

  const order = { untrained: 0, partial: 1, trained: 2 };
  const products = [...(st.products || [])].sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
  const waiting = st.waiting_for_update || [];
  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">Training on this cabinet</h2>
        <dl className="grid grid-cols-[12rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-500">Type</dt><dd><Link className="text-sky-700 hover:underline" to={`/types/${st.cabinet_type_id}`}>{st.cabinet_type_name}</Link></dd>
          <dt className="text-slate-500">Every product trained</dt><dd className={st.trusted ? "text-emerald-700" : "text-amber-700"}>{st.trusted ? "yes" : "no"}</dd>
          <dt className="text-slate-500">Runtime can train</dt><dd>{st.device_supports_training ? "yes" : "no (older runtime)"}</dd>
          <dt className="text-slate-500">Visit in progress</dt><dd>{st.live ? <Fields obj={st.live} /> : "none"}</dd>
          <dt className="text-slate-500">Learning after a visit</dt><dd>{st.collecting ? <Fields obj={st.collecting} /> : "none"}</dd>
          <dt className="text-slate-500">Last visit</dt>
          <dd>{st.last_visit ? <span className={st.last_visit.outcome === "collected" ? "" : "text-amber-700"}>{st.last_visit.outcome} <Fields obj={Object.fromEntries(Object.entries(st.last_visit).filter(([k]) => k !== "outcome"))} /></span> : "none in the log"}</dd>
          <dt className="text-slate-500">Waiting for Update shelves</dt>
          <dd className="text-xs">{waiting.length ? waiting.map((w) => nameOf(names, typeof w === "string" ? w : w.product_uuid)).join(", ") : "nothing"}</dd>
        </dl>
      </div>
      <div className="card overflow-x-auto">
        <h2 className="mb-2 text-sm font-semibold">Products of the layout</h2>
        <table className="table">
          <thead><tr><th>Product</th><th>Status</th><th>Rows by camera</th><th>Required</th><th>Separability</th><th>Nearest look-alike</th><th>Last trained</th></tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.product_uuid}>
                <td>{nameOf(names, p.product_uuid)}</td>
                <td className={p.status === "trained" ? "text-emerald-700" : p.status === "partial" ? "text-amber-700" : "text-rose-700"}>{p.status}</td>
                <td className="font-mono text-xs">{Object.entries(p.rows_by_camera || {}).map(([c, n]) => `${c}: ${n}`).join(" · ") || "—"}</td>
                <td className="text-xs">{(p.required_cameras || []).join(", ")}</td>
                <td className="text-xs">{p.separability != null ? p.separability.toFixed(2) : "—"}{p.confident === false ? " (weak)" : ""}</td>
                <td className="text-xs">{p.nearest_product ? nameOf(names, p.nearest_product) : "—"}</td>
                <td className="text-xs">{p.last_trained_at ? ago(p.last_trained_at) : "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-slate-500">A camera counts at {st.min_rows} rows; separability under {st.confident_margin} is weak (a look-alike is close). Visits are run from the capture app.</p>
      </div>
    </div>
  );
}
